import { useCallback } from 'react'
import { findLevelNodes, resolveTopLevelNodeId } from './processTree'
import { isExternalEffective } from './jobExternalCharacter'

/**
 * The canvas node data patched in place, the technique of `applyNodeSelection`/`applyExternalMark`: a
 * rebuild would re-trigger `fitView` (pan/zoom), forbidden by RN09.4/RN09.5.
 */
const patchNodeData = (nodes, nodeId, patch) => (nodes || []).map((node) => (node.id === nodeId ? { ...node, data: { ...node.data, ...patch } } : node))

/**
 * The gates of the operation window of Tree.
 */
export const resolveProcessTreeJobGates = ({ editing, canEnterEditMode, canSeeJobs, canSeeTasks, canSeeMaterial, isJobRow, node, currentNodeId, topLevelId, nested }) => {
  const editAllowed = editing || canEnterEditMode
  // The placeholder of a local copy whose source could not be read is overwritten by Sun on save, so it is not
  // edited here either (the graph's panel says the same, `Workflow.jsx` `hasPendingLocalCopy`).
  const overwrittenOnSave = node?.operationLink?.intent === 'COPY' && node?.operationLink?.keepEditedContent !== true
  const canEdit = editAllowed && canSeeJobs && isJobRow && !!node && currentNodeId === topLevelId && !overwrittenOnSave
  return {
    editAllowed,
    canEdit,
    canEditTasks: canEdit && canSeeTasks,
    canEditMaterial: canSeeMaterial && editAllowed && !nested && isJobRow && !!node && !isExternalEffective(node?.jobReference)
  }
}

/**
 * The writers of the operation window of Tree. A NESTED job is
 * adopted into the model first (`ADOPT_SUBWORKFLOW`) and patched by id (`UPDATE_NESTED_JOB`); a first-level
 * one goes through the graph's own actions and its card is patched in place.
 */
export const useProcessTreeJobWriters = ({
  workflow,
  effectiveWorkflow,
  editing,
  onEnterEditMode,
  dispatchWorkflowData,
  updateJobReference,
  updateTaskList,
  nodes,
  setNodes,
  inspectedNodeId,
  nested
}) => {
  const ensureEditing = useCallback(() => {
    if (!editing) {
      onEnterEditMode()
    }
  }, [editing, onEnterEditMode])

  const writeNestedJob = useCallback((nodeId, changes) => {
    ensureEditing()
    const topLevelId = resolveTopLevelNodeId(nodeId, effectiveWorkflow?.nodes)
    const modelContainer = (workflow?.nodes || []).find((node) => node.id === topLevelId)
    const effectiveContainer = (effectiveWorkflow?.nodes || []).find((node) => node.id === topLevelId)
    if (!modelContainer || !effectiveContainer) {
      return
    }
    const modelHoldsJob = findLevelNodes(nodeId, [modelContainer]).some((node) => node.id === nodeId)
    if (!modelHoldsJob) {
      dispatchWorkflowData({ type: 'ADOPT_SUBWORKFLOW', nodeId: topLevelId, subWorkflow: effectiveContainer.subWorkflow })
    }
    dispatchWorkflowData({ type: 'UPDATE_NESTED_JOB', nodeId, changes })
  }, [ensureEditing, effectiveWorkflow, workflow, dispatchWorkflowData])

  const writeJobReference = useCallback((jobReference) => {
    if (nested) {
      writeNestedJob(inspectedNodeId, { jobReference })
      return
    }
    ensureEditing()
    updateJobReference(jobReference)
  }, [nested, inspectedNodeId, writeNestedJob, ensureEditing, updateJobReference])

  const writeTaskList = useCallback((taskReferences) => {
    if (nested) {
      writeNestedJob(inspectedNodeId, { taskReferences })
      return
    }
    ensureEditing()
    updateTaskList(taskReferences)
  }, [nested, inspectedNodeId, writeNestedJob, ensureEditing, updateTaskList])

  const isTopLevel = useCallback((nodeId) => resolveTopLevelNodeId(nodeId, effectiveWorkflow?.nodes) === nodeId, [effectiveWorkflow])

  const updateNodeName = useCallback((nodeId, nodeName) => {
    if (!isTopLevel(nodeId)) {
      writeNestedJob(nodeId, { name: nodeName })
      return
    }
    ensureEditing()
    dispatchWorkflowData({ type: 'UPDATE_NODE_NAME', nodeName, nodeId })
    setNodes(patchNodeData(nodes, nodeId, { nodeName }))
  }, [isTopLevel, writeNestedJob, ensureEditing, dispatchWorkflowData, setNodes, nodes])

  const updateNodeCode = useCallback((nodeId, nodeCode) => {
    if (!isTopLevel(nodeId)) {
      writeNestedJob(nodeId, { code: nodeCode })
      return
    }
    ensureEditing()
    dispatchWorkflowData({ type: 'UPDATE_NODE_CODE', nodeCode, nodeId })
    setNodes(patchNodeData(nodes, nodeId, { nodeCode }))
  }, [isTopLevel, writeNestedJob, ensureEditing, dispatchWorkflowData, setNodes, nodes])

  const updateTechnology = useCallback((nodeId, technology) => {
    if (!isTopLevel(nodeId)) {
      writeNestedJob(nodeId, { technology })
      return
    }
    ensureEditing()
    dispatchWorkflowData({ type: 'UPDATE_JOB_TECHNOLOGY', technology, nodeId })
    setNodes(patchNodeData(nodes, nodeId, { technology, referenceName: technology?.name || '' }))
  }, [isTopLevel, writeNestedJob, ensureEditing, dispatchWorkflowData, setNodes, nodes])

  return { writeJobReference, writeTaskList, updateNodeName, updateNodeCode, updateTechnology }
}
